import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import isObject from "lodash/isObject";
import transform from "lodash/transform";
import type { Shop, ShopConfiguration } from "@/shared/types/shop";

// Stesse regole del legacy Utils: undefined !== null, quindi uno shop senza il campo conta come completato.
export const isCompleted = (shop: Shop): boolean => shop.last_index_start_time !== null;
export const isConfiguring = (shop: Shop): boolean =>
  shop.last_sync_start_time != null && shop.last_index_start_time == null;
export const isToBeConfigured = (shop: Shop): boolean => shop.last_sync_start_time == null;

/** Gli shop che lo shell mostra: completati, poi in configurazione (getDisplayedWebsites). */
export function displayedShops(shops: readonly Shop[]): Shop[] {
  return [...shops.filter(isCompleted), ...shops.filter(isConfiguring)];
}

/** Gli shop che si configurano dal wizard: mai sincronizzati e assenti da displayedShops. */
export function pendingShops(shops: readonly Shop[]): Shop[] {
  return shops.filter((shop) => isToBeConfigured(shop) && !isCompleted(shop));
}

/** Primo shop da selezionare al bootstrap: primo completato, altrimenti primo in configurazione. */
export function initialShop(shops: readonly Shop[]): Shop | undefined {
  return shops.find(isCompleted) ?? shops.find(isConfiguring);
}

/** Come Utils.beautifyWebsite: replace singolo, non globale. */
export function beautifyWebsite(url: string): string {
  return url.replace("http://", "").replace("https://", "").replace("www.", "");
}

export function shopDisplayName(shop: Shop | undefined): string {
  if (!shop) return "";
  const url = shop.url || "";
  const label = (shop.label || "").trim();
  if (label && label !== url) return label;
  return url ? beautifyWebsite(url) : "";
}

/** Utils.newEmptyConfig(): la configurazione "niente selezionato" e la sentinella per riconoscerla. */
export function emptyConfiguration(
  websiteName: string | null = null,
  id: number | string = "",
): ShopConfiguration {
  return {
    sector: null,
    cms: { type: "", version: "" },
    url: websiteName as unknown as string,
    id,
    globalSettings: false,
    setup: {
      query: [],
      results: [],
      facets: [],
      dynamicFacets: false,
      seoSettings: false,
      ga4: { isActive: false },
      boosting: [],
      config: { selectorId: "", productUrl: "" },
      layerVersion: "latest",
    },
    layout: {
      general: {},
      desktop: { layout: 1, logoUrl: "", typing: 1, minChars: 4, paging: 1, perPage: 1 },
      mobile: { minChars: 4, paging: 1, perPage: 2, barcode: false },
      builder: { isActive: false },
    },
    syncing: false,
    synced: false,
    completed: false,
    counter: 0,
    monthly: 0,
    fineTuning: { nlq: "off", ips: [], stopwords: [], synonyms: [], weights: {} },
  };
}

export function isEmptyConfiguration(config: ShopConfiguration | undefined): boolean {
  return isEqual(config, emptyConfiguration());
}

/** Utils.getConfigurationFromWebsite: cerca per url+lingua; [] -> config vuota, nessun match -> {} (asimmetria del legacy). */
export function savedShopFor(shops: readonly Shop[], config: ShopConfiguration): ShopConfiguration {
  if (shops.length === 0) return emptyConfiguration();
  const found = shops.find((item) => item.url == config.url && item.language_iso == config.language_iso);
  return found ? { ...found } : ({} as ShopConfiguration);
}

type Diff = Record<string, unknown>;

/** Utils.difference: differenza ricorsiva di object rispetto a base. */
function difference(object: Record<string, unknown>, base: Record<string, unknown>): Diff {
  return transform(object, (result: Diff, value, key) => {
    if (!isEqual(value, base[key])) {
      result[key] =
        isObject(value) && isObject(base[key])
          ? difference(value as Record<string, unknown>, base[key] as Record<string, unknown>)
          : value;
    }
  }) as Diff;
}

/** Negazione di Utils.checkChanges: true se la config in editing differisce davvero da quella salvata. */
export function hasRealChanges(shops: readonly Shop[], config: ShopConfiguration): boolean {
  const diff = difference(
    savedShopFor(shops, config) as Record<string, unknown>,
    config as Record<string, unknown>,
  );
  delete diff["last_index_end_time"];
  delete diff["last_index_start_time"];
  const layout = diff["layout"] as { facets?: unknown[] } | undefined;
  if (layout && Array.isArray(layout.facets) && layout.facets.every((item) => isEmpty(item))) {
    delete layout.facets;
    if (isEmpty(layout)) delete diff["layout"];
  }
  return !isEmpty(diff);
}

const asInt = (value: unknown): number => Number.parseInt(String(value), 10) || 0;

export const totalSkus = (shops: readonly Shop[]): number =>
  shops.filter(isCompleted).reduce((sum, shop) => sum + asInt(shop.skus), 0);
export const totalMonthlyRequests = (shops: readonly Shop[]): number =>
  shops.filter(isCompleted).reduce((sum, shop) => sum + asInt(shop.requests), 0);
